import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { Plus, LogOut, Bell, MessagesSquare, ShieldCheck } from "lucide-react";
import Avatar from "@/components/Avatar";
import Logo from "@/components/Logo";

export default function Navbar() {
  const { user, profile, signOut, isAdmin } = useAuth();
  const { unreadNotifs, unreadMsgs } = useRealtime();
  const navigate = useNavigate();

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 bg-paper border-b-[3px] border-ink"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="inline-flex items-center group">
          <Logo heightClassName="h-7 md:h-9" testid="navbar-logo" />
        </Link>

        <div className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest mx-auto">
          <NavPill to="/feed">Feed</NavPill>
          <NavPill to="/map">Map</NavPill>
          {user && <NavPill to="/my-events">My events</NavPill>}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="navbar-admin-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-[2.5px] border-ink bg-lime text-ink font-bold transition-transform hover:-translate-y-px"
            >
              <ShieldCheck size={14} strokeWidth={2.5} /> Admin
            </NavLink>
          )}
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <IconCircle to="/messages" testid="nav-messages" badge={unreadMsgs} icon={MessagesSquare} aria="Messages" />
              <IconCircle to="/notifications" testid="nav-notifications" badge={unreadNotifs} icon={Bell} aria="Notifications" />
              <button
                data-testid="navbar-create-button"
                onClick={() => navigate("/create")}
                className="hidden md:inline-flex items-center gap-2 bg-lime text-ink border-[3px] border-ink rounded-full px-4 py-2 text-xs font-extrabold uppercase tracking-widest shadow-pop active:translate-x-[2px] active:translate-y-[2px] active:shadow-pop-active transition-[transform,box-shadow]"
              >
                <Plus size={16} strokeWidth={2.8} />
                <span>Create</span>
              </button>
              <Link
                to="/profile"
                data-testid="navbar-profile"
                title={user.email}
                className="hidden md:inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full pl-1 pr-3 py-1 hover:bg-ink hover:text-paper transition-colors"
              >
                <Avatar src={profile?.avatar_url} name={profile?.full_name || profile?.display_name || user.email} size="xs" />
                <span className="max-w-[110px] truncate text-xs font-bold uppercase tracking-widest">
                  {profile?.username ? `@${profile.username}` : profile?.display_name || user.email?.split("@")[0]}
                </span>
              </Link>
              <button
                data-testid="navbar-logout"
                onClick={async () => { await signOut(); navigate("/"); }}
                className="hidden md:inline-flex items-center justify-center w-10 h-10 border-[2.5px] border-ink rounded-full hover:bg-ink hover:text-paper transition-colors"
                aria-label="Log out"
              >
                <LogOut size={16} strokeWidth={2} />
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="navbar-create-button"
                onClick={() => navigate("/login?next=/create")}
                className="inline-flex items-center gap-2 bg-lime text-ink border-[3px] border-ink rounded-full px-4 py-2 text-xs font-extrabold uppercase tracking-widest shadow-pop active:translate-x-[2px] active:translate-y-[2px] active:shadow-pop-active transition-[transform,box-shadow]"
              >
                <Plus size={16} strokeWidth={2.8} /> <span className="hidden sm:inline">Create</span>
              </button>
              <Link
                to="/login"
                data-testid="navbar-login"
                className="border-[2.5px] border-ink rounded-full px-4 py-2 text-xs font-extrabold uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
              >
                Log in
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function NavPill({ to, children }) {
  return (
    <Link
      to={to}
      className="px-4 py-2 rounded-full border-[2.5px] border-transparent hover:border-ink text-ink font-bold transition-colors"
    >
      {children}
    </Link>
  );
}

function IconCircle({ to, icon: Icon, badge, testid, aria }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      aria-label={aria}
      className="relative inline-flex items-center justify-center w-10 h-10 border-[2.5px] border-ink rounded-full hover:bg-ink hover:text-paper transition-colors"
    >
      <Icon size={16} strokeWidth={2} />
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-lime text-ink text-[10px] font-extrabold leading-[18px] text-center rounded-full border-2 border-ink"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}
